import type { RoutePortalType } from '@/types/route_management';

export const managedRuntimeDomains = [
  'finance',
  'parameter',
  'business',
  'application',
  'infrastructure',
  'project',
  'iso22301',
  'iso2003',
  'cbm',
  'compliance',
  'sourcing',
  'pmo',
  'ai',
  'quenotte',
  'crisis',
  // Lot MPL-1 (12.09) : menu CORE (todos, macroplans) — pages navigation-only.
  'core',
  // Module event-management (17.09) — portée ESPACE DE TRAVAIL, sans année
  // (include_year = FALSE, V20260918_0400). Écrans CRUD résolus par
  // event_route_registry.
  'event',
  // Module Studio (UPT-2079 lot L0, 11.10) — portée ESPACE DE TRAVAIL, sans
  // année (V20261020_5120). Écrans résolus par module_studio_route_registry
  // (Atelier, fiches dossier, plans et exécutions de test).
  'module-studio',
  'pret',
  'personal-finance',
  'learning',
  'engagement',
] as const;

export type ManagedRuntimeDomain = typeof managedRuntimeDomains[number];
export type RuntimeRouteScope = 'none' | 'workspace' | 'year' | 'workspace-year';
export type RuntimeRouteMode = 'list' | 'new' | 'item' | 'history' | 'unknown';

export type ParsedRuntimeRoutePath = {
  pathname: string;
  segments: string[];
  portalPrefix: RoutePortalType;
  workspaceId: string | null;
  year: string | null;
  domain: ManagedRuntimeDomain | null;
  entity: string | null;
  scope: RuntimeRouteScope;
  mode: RuntimeRouteMode;
  resourceId: string | null;
  suffixSegments: string[];
  matched: boolean;
  supportedShape: boolean;
};

const managedRuntimeDomainSet = new Set<string>(managedRuntimeDomains);

const normalizePathname = (pathname: string) => {
  const normalized = pathname.trim().replace(/[?#].*$/, '');
  if (normalized.length === 0) {
    return '/';
  }
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

export const splitPathSegments = (pathname: string) =>
  normalizePathname(pathname)
    .split('/')
    .filter(Boolean);

export const isManagedRuntimeDomain = (
  value?: string | null
): value is ManagedRuntimeDomain =>
  typeof value === 'string' && managedRuntimeDomainSet.has(value.trim().toLowerCase());

export const isYearSegment = (value?: string | null) =>
  typeof value === 'string' && /^\d{4}$/.test(value.trim());

const normalizeSegment = (value?: string | null) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const toScope = (
  workspaceId: string | null,
  year: string | null
): RuntimeRouteScope => {
  if (workspaceId && year) {
    return 'workspace-year';
  }
  if (workspaceId) {
    return 'workspace';
  }
  if (year) {
    return 'year';
  }
  return 'none';
};

const KNOWN_PORTAL_PREFIXES = new Set(['portal', 'adminportal', 'make']);

const resolveManagedSegments = (segments: string[]) => {
  // Determine the starting offset:
  // - If first segment is a known portal prefix (portal/adminportal/make), skip it
  // - Otherwise the URL is legacy format (/:workspaceId/:year/:domain/...)
  const firstSegment = segments[0];
  const offset = firstSegment && KNOWN_PORTAL_PREFIXES.has(firstSegment) ? 1 : 0;
  const tail = segments.slice(offset);

  // Pattern: :workspaceId/:year/:domain/:entity
  if (tail.length >= 3 && isYearSegment(tail[1]) && isManagedRuntimeDomain(tail[2])) {
    return {
      workspaceId: tail[0] ?? null,
      year: tail[1] ?? null,
      domain: normalizeSegment(tail[2]) as ManagedRuntimeDomain,
      entity: normalizeSegment(tail[3]) || null,
      nextIndex: offset + 3,
    };
  }

  // Pattern: :year/:domain/:entity
  if (tail.length >= 2 && isYearSegment(tail[0]) && isManagedRuntimeDomain(tail[1])) {
    return {
      workspaceId: null,
      year: tail[0] ?? null,
      domain: normalizeSegment(tail[1]) as ManagedRuntimeDomain,
      entity: normalizeSegment(tail[2]) || null,
      nextIndex: offset + 2,
    };
  }

  // Pattern: :workspaceId/:domain/:entity (no year)
  if (tail.length >= 2 && isManagedRuntimeDomain(tail[1])) {
    return {
      workspaceId: tail[0] ?? null,
      year: null,
      domain: normalizeSegment(tail[1]) as ManagedRuntimeDomain,
      entity: normalizeSegment(tail[2]) || null,
      nextIndex: offset + 2,
    };
  }

  // Pattern: :domain/:entity (no workspace, no year)
  if (tail.length >= 1 && isManagedRuntimeDomain(tail[0])) {
    return {
      workspaceId: null,
      year: null,
      domain: normalizeSegment(tail[0]) as ManagedRuntimeDomain,
      entity: normalizeSegment(tail[1]) || null,
      nextIndex: offset + 1,
    };
  }

  return null;
};

export const buildRuntimeRoutePattern = (options: {
  portalPrefix: RoutePortalType;
  scope: RuntimeRouteScope;
  domain: string;
  entity: string;
  suffix?: string;
}) => {
  const segments = [options.portalPrefix];

  if (options.scope === 'workspace' || options.scope === 'workspace-year') {
    segments.push(':workspaceId');
  }

  if (options.scope === 'year' || options.scope === 'workspace-year') {
    segments.push(':year');
  }

  segments.push(options.domain.trim().toLowerCase());
  segments.push(options.entity.trim().toLowerCase());

  const suffix = typeof options.suffix === 'string' ? options.suffix.trim() : '';
  if (!suffix) {
    return `/${segments.join('/')}`;
  }

  const normalizedSuffix = suffix.replace(/^\/+/, '');
  return `/${segments.join('/')}/${normalizedSuffix}`;
};

/**
 * Parse an ACTUAL browser URL at runtime into backend-facing routing params.
 *
 * Intentionally narrower than the design-time `parseRoutePathDefinition`
 * (app/utils/route_management.ts): that parser treats segment[0] as an
 * arbitrary, author-supplied portal prefix, but at runtime a legacy
 * `/:workspaceId/:year/:domain/...` URL shares the same shape as a dynamic
 * `/:prefix/:year/:domain/...` URL. Treating an unknown first segment as a
 * portal prefix would forward a workspaceId to the backend as `portalType`,
 * which returns 400. So only the fixed prefix set (portal/adminportal/make) is
 * recognized; any other first segment is parsed as legacy and the prefix falls
 * back to `fallbackPortalPrefix`. The canonical detail mode is `'item'`
 * ('detail' exists only as a legacy input alias normalized in
 * route_management_normalizers.ts). See UPT-19 for the full reconciliation.
 */
export const parseRuntimeRoutePath = (
  pathname: string,
  fallbackPortalPrefix: RoutePortalType = 'portal'
): ParsedRuntimeRoutePath => {
  const normalizedPathname = normalizePathname(pathname);
  const segments = splitPathSegments(pathname);
  const rawFirstSegment = segments[0] ?? fallbackPortalPrefix;

  // If the first segment IS a known portal prefix, use it directly.
  // Otherwise, the URL is in legacy format (/:workspaceId/:year/:domain/...)
  // and we must infer the portal type instead of passing the workspaceId
  // as portalType to backend APIs (which causes 400 Bad Request).
  // UPT-1060: warn when falling back — legacy URLs should be redirected, not silently inferred.
  const isKnownPrefix = rawFirstSegment === 'portal' || rawFirstSegment === 'adminportal' || rawFirstSegment === 'make';

  const managedSegments = resolveManagedSegments(segments);

  // UPT-1562: when the first segment is a managed domain acting as its own portal
  // prefix (e.g. /parameter/workspace-list), infer portalPrefix from the domain
  // instead of falling back to 'portal'. Only applies when there is no workspace
  // or year segment (Pattern 4 match), so a legacy /:workspaceId/:year/:domain
  // URL whose workspaceId coincidentally matches a domain name is not affected.
  const isDomainAsPrefix =
    !isKnownPrefix &&
    managedSegments !== null &&
    managedSegments.workspaceId === null &&
    managedSegments.year === null &&
    managedSegments.domain === rawFirstSegment;

  if (!isKnownPrefix && !isDomainAsPrefix) {
    console.warn(
      `[parseRuntimeRoutePath] Unknown URL prefix "${rawFirstSegment}" in "${pathname}" — ` +
      `falling back to "${fallbackPortalPrefix}". This URL may be missing a portal prefix. ` +
      `If this is a user-navigated URL, the caller should redirect to the canonical prefixed form ` +
      `instead of silently inferring the portal type. (UPT-1060)`
    );
  }
  const portalPrefix: RoutePortalType = isKnownPrefix
    ? rawFirstSegment
    : isDomainAsPrefix
      ? rawFirstSegment
      : fallbackPortalPrefix;

  if (!managedSegments || !managedSegments.entity) {
    return {
      pathname: normalizedPathname,
      segments,
      portalPrefix,
      workspaceId: managedSegments?.workspaceId ?? null,
      year: managedSegments?.year ?? null,
      domain: managedSegments?.domain ?? null,
      entity: managedSegments?.entity ?? null,
      scope: toScope(managedSegments?.workspaceId ?? null, managedSegments?.year ?? null),
      mode: 'unknown',
      resourceId: null,
      suffixSegments: [],
      matched: false,
      supportedShape: false,
    };
  }

  const suffixSegments = segments.slice(managedSegments.nextIndex + 1);
  let mode: RuntimeRouteMode = 'unknown';
  let resourceId: string | null = null;

  if (managedSegments.entity === 'integration-job-history') {
    if (suffixSegments.length === 1) {
      mode = 'history';
      resourceId = suffixSegments[0] ?? null;
    }
  } else if (suffixSegments.length === 0) {
    mode = 'list';
  } else if (suffixSegments.length === 1 && suffixSegments[0] === 'new') {
    mode = 'new';
  } else if (suffixSegments.length === 1) {
    mode = 'item';
    resourceId = suffixSegments[0] ?? null;
  }

  const scope = toScope(managedSegments.workspaceId, managedSegments.year);

  return {
    pathname: normalizedPathname,
    segments,
    portalPrefix,
    workspaceId: managedSegments.workspaceId,
    year: managedSegments.year,
    domain: managedSegments.domain,
    entity: managedSegments.entity,
    scope,
    mode,
    resourceId,
    suffixSegments,
    matched: true,
    supportedShape: mode !== 'unknown',
  };
};

/**
 * Domaines gérés à portée ESPACE DE TRAVAIL : leur grammaire de route ne
 * comporte PAS de segment année (/{portal}/:workspaceId/{domaine}/{entité}).
 *
 * Volontairement restreint à `event` (17.09) : les autres domaines sans année
 * (crisis, quenotte, core, business/application/infrastructure) tolèrent déjà
 * une année dans l'URL et sont atteints par des menus qui en produisent une —
 * les réécrire d'office déplacerait des URL qui fonctionnent aujourd'hui.
 * Élargir cette liste = un domaine à la fois, avec sa recette.
 */
export const workspaceScopedRuntimeDomains = new Set<ManagedRuntimeDomain>(['event', 'module-studio', 'pret', 'personal-finance', 'learning', 'engagement']);

/**
 * Réécrit une URL d'entité qui porte une année EN TROP sur un domaine à portée
 * espace de travail : /portal/{ws}/2026/event/events → /portal/{ws}/event/events.
 *
 * Pendant, dans l'autre sens, de `buildScopelessPortalRedirect`
 * (runtime_managed_route_entry.tsx) : là une portée manque et on l'injecte,
 * ici une portée est en trop et on la retire. Sans cette réécriture, l'URL
 * année sort de la grammaire des routes déclarées dans routes.ts pour le
 * domaine (qui n'en déclare que la forme sans année) et l'utilisateur tombe
 * sur « Page not found » (recette Olivier 17.09).
 *
 * Ne s'applique QU'AUX URL d'entité : la page d'accueil de domaine
 * (/portal/{ws}/{an}/{domaine}, sans entité) garde sa forme — c'est celle que
 * ModuleLandingPage sait monter.
 */
export const buildYearlessDomainRedirect = (
  parsedPath: Pick<
    ParsedRuntimeRoutePath,
    'portalPrefix' | 'workspaceId' | 'year' | 'domain' | 'entity' | 'suffixSegments'
  >
): string | null => {
  const { domain, entity, workspaceId, year } = parsedPath;
  if (!domain || !workspaceScopedRuntimeDomains.has(domain)) return null;
  if (!entity || !workspaceId || !year) return null;

  const prefix = parsedPath.portalPrefix ?? 'portal';
  const tail = [domain, entity, ...parsedPath.suffixSegments];
  return `/${prefix}/${workspaceId}/${tail.join('/')}`;
};

export const buildRuntimeRoutePatternFromParsed = (
  parsedPath: Pick<ParsedRuntimeRoutePath, 'portalPrefix' | 'scope' | 'domain' | 'entity'>,
  suffix = ''
) => {
  if (!parsedPath.domain || !parsedPath.entity) {
    return null;
  }

  return buildRuntimeRoutePattern({
    portalPrefix: parsedPath.portalPrefix,
    scope: parsedPath.scope,
    domain: parsedPath.domain,
    entity: parsedPath.entity,
    suffix,
  });
};

/**
 * Substitute the dynamic path tokens (`:workspaceId`, `:year`) in a route path
 * with concrete values from the current portal context.
 *
 * Navigation nodes and route definitions store template paths such as
 * `/portal/:workspaceId/:year/ai/scans/new`. The runtime navigation manifest
 * and DynamicTopNav navigate to `routePath` verbatim, so without this
 * substitution the browser would be sent to a literal `:workspaceId` segment.
 *
 * Only whole-segment tokens are replaced (so a segment that merely contains the
 * text is left alone), a token with no available value is left untouched (a
 * best-effort resolution is safer than emitting a broken concrete URL), and any
 * query/hash suffix is preserved. Values are substituted verbatim: they
 * originate from an existing URL path segment or a workspace slug and are
 * already path-safe, so re-encoding here would risk corrupting them.
 */
export const substituteRuntimeRouteTokens = (
  path: string | null | undefined,
  context: { workspaceId?: string | null; year?: string | null }
): string | undefined => {
  if (!path) {
    return path ?? undefined;
  }

  const tokenValues: Record<string, string | null | undefined> = {
    ':workspaceId': context.workspaceId,
    ':year': context.year,
  };

  // Legacy aliases found in DB-authored navigation nodes. Canonical token is
  // ':workspaceId' — warn so the data gets fixed instead of relying on the alias.
  // TODO: remove after navigation route_path values are normalized (RR7 route management)
  const aliasTokens: Record<string, string | null | undefined> = {
    ':workspaceid': context.workspaceId,
    ':ws': context.workspaceId,
  };

  // Keep any query/hash suffix out of the segment mapping.
  const suffixMatch = path.match(/[?#].*$/);
  const suffix = suffixMatch ? suffixMatch[0] : '';
  const pathnameOnly = suffix ? path.slice(0, path.length - suffix.length) : path;

  const substituted = pathnameOnly
    .split('/')
    .map((segment) => {
      const value = tokenValues[segment];
      if (value) {
        return value;
      }
      const aliasValue = aliasTokens[segment];
      if (aliasValue) {
        console.warn(`[navigation] legacy route token '${segment}' in '${path}' — use ':workspaceId' (RR7 route management)`);
        return aliasValue;
      }
      // Fail loudly (UPT-1060): an unsubstituted ':token' would otherwise
      // silently leak into the URL and produce empty API results.
      if (segment.startsWith(':')) {
        console.warn(`[navigation] unsubstituted route token '${segment}' in '${path}'`, context);
      }
      return segment;
    })
    .join('/');

  return `${substituted}${suffix}`;
};

/**
 * V2 Backward Compat: Try to derive a singular slug from a plural entity slug.
 * Used to redirect old plural URLs like /budgets/new → /budget/new.
 * Returns undefined if the slug doesn't look plural or can't be singularized.
 */
export const singularizeEntitySlug = (slug: string): string | undefined => {
  if (!slug || /^\d{4}$/.test(slug)) return undefined;
  const domainSlugs = new Set([
    'finance', 'parameter', 'pmo', 'sourcing', 'cbm', 'admin',
    'business', 'application', 'infrastructure', 'compliance',
    'project', 'iso22301', 'iso2003', 'ai', 'quenotte', 'crisis',
  ]);
  if (domainSlugs.has(slug)) return undefined;

  // Special cases: irregular plurals or non-obvious singular forms
  const singularMap: Record<string, string> = {
    budgets: 'budget',
    budgetlines: 'budgetline',
    budgetlinedetails: 'budgetlinedetail',
    budgetconsumptions: 'budgetconsumption',
    budgetrequests: 'budgetrequest',
    budgetrequestextras: 'budgetrequestextra',
    orders: 'order',
    psfs: 'psf',
    psflines: 'psfline',
    xrfbudgetpsflines: 'xrfbudgetpsfline',
    invoices: 'invoice',
    invoicelines: 'invoiceline',
    invoicelinedetails: 'invoicelinedetail',
    reclassifies: 'reclassify',
    totalbyaccounts: 'totalbyaccount',
    cas: 'ca',
    glaccounts: 'glaccount',
    repartitionkeys: 'repartitionkey',
    suppliers: 'supplier',
    supplierconsumptions: 'supplierconsumption',
    contracts: 'contract',
    servicetypes: 'servicetype',
    projects: 'project',
    projectreportings: 'projectreporting',
    bcms: 'bcm',
    beals: 'beal',
    seals: 'seal',
    slas: 'sla',
    teams: 'team',
    parameters: 'parameter',
    auditlogs: 'auditlog',
    'integration-jobs': 'integration-job',
    fileloadings: 'fileloading',
    fileexports: 'fileexport',
    users: 'user',
    roles: 'role',
    groups: 'group',
    scans: 'scan',
  };

  if (singularMap[slug]) return singularMap[slug];

  // Generic heuristic fallback
  if (slug.endsWith('ies')) return slug.slice(0, -3) + 'y';
  if (slug.endsWith('es') && !slug.endsWith('sses')) return slug.slice(0, -2);
  if (slug.endsWith('s') && !slug.endsWith('ss')) return slug.slice(0, -1);
  return undefined;
};

/**
 * V2 Backward Compat: Check if a parsed route path uses a plural entity slug
 * for a new/item route (which should use singular per v2 convention).
 * Returns the corrected singular URL or null if no redirect needed.
 */
export const tryV2Redirect = (parsedPath: ParsedRuntimeRoutePath): string | null => {
  // Only redirect for new/item routes, not list routes (list stays plural)
  if (parsedPath.mode !== 'new' && parsedPath.mode !== 'item') return null;
  if (!parsedPath.entity) return null;

  const singular = singularizeEntitySlug(parsedPath.entity);
  if (!singular || singular === parsedPath.entity) return null;

  // Rebuild the URL with the singular slug
  const prefix = parsedPath.portalPrefix ?? 'portal';
  const ws = parsedPath.workspaceId ?? ':workspaceId';
  const yr = parsedPath.year;
  const domain = parsedPath.domain;
  const suffix = parsedPath.mode === 'new' ? '/new' : `/${parsedPath.resourceId ?? ':id'}`;

  if (yr) {
    return `/${prefix}/${ws}/${yr}/${domain}/${singular}${suffix}`;
  }
  return `/${prefix}/${ws}/${domain}/${singular}${suffix}`;
};
